/**
 * Маршрутные стражи (auth-and-roles): закрывают приватные разделы, разводят публичные
 * экраны входа/регистрации и экран первичной настройки по состоянию сессии и роли.
 */
import { type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from './auth-context'
import { FullScreenLoading } from './loading'
import { isTeamRole } from './nav'
import type { Role } from '@/lib/api'

/**
 * Приватная область: пускает только при активной сессии. Клиента-компанию без выбранной
 * организации (multi-organization-login) ведёт в окно выбора — кроме самого окна
 * (`organizationOptional`).
 */
export function RequireAuth({
  children,
  organizationOptional = false,
}: {
  children: ReactNode
  organizationOptional?: boolean
}) {
  const { status, needsOrganization } = useAuth()
  if (status === 'loading') return <FullScreenLoading />
  if (status === 'needs-setup') return <Navigate to="/setup" replace />
  if (status === 'unauthenticated') return <Navigate to="/login" replace />
  if (needsOrganization && !organizationOptional) return <Navigate to="/organizations" replace />
  return <>{children}</>
}

/** Публичные экраны входа/регистрации/сброса: вошедшего уводим в кабинет. */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth()
  if (status === 'loading') return <FullScreenLoading />
  if (status === 'needs-setup') return <Navigate to="/setup" replace />
  if (status === 'authenticated') return <Navigate to="/" replace />
  return <>{children}</>
}

/** Экран первичной настройки доступен только пока система пуста. */
export function SetupRoute({ children }: { children: ReactNode }) {
  const { status } = useAuth()
  if (status === 'loading') return <FullScreenLoading />
  if (status === 'needs-setup') return <>{children}</>
  return <Navigate to="/" replace />
}

/** Ограничение по роли внутри кабинета (например, командные разделы). */
export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { context } = useAuth()
  if (!context || !roles.includes(context.user.role)) return <Navigate to="/" replace />
  return <>{children}</>
}

/** Удобный страж «только команда». */
export function RequireTeam({ children }: { children: ReactNode }) {
  const { context } = useAuth()
  if (!context || !isTeamRole(context.user.role)) return <Navigate to="/" replace />
  return <>{children}</>
}
